import {
  type AccountTransactionChildWithDerivedStatuses,
  type TokenTransferChildWithContext,
  SignableType,
} from '@mls-wallet/shared-types';
import { ArrowRight } from 'lucide-react';
import {
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Card, CardContent } from '@/components/ui/card';
import { StatusBadge } from '@/components/shared/status-badge';
import { MemberDisplay } from '@/components/members/member-display';
import { TokenTransferLimit } from '@/components/shared/token-transfer-limit';
import {
  SectionHeader,
  SectionHeaderText,
} from '@/components/shared/section-header';
import { cn } from '@/lib/utils';
import { formatName } from '@/lib/name';
import { toHumanReadableTokenAmount } from '@/utils/tokenAmountUtils';
import { OriginDisplay, DestinationDisplay } from './transaction-displays';
import { TransactionDetailTitle } from './transaction-detail-title';
import { TransactionDetailTypeDisplay } from './transaction-detail-type-display';
import { ContractInteractionDetails } from './contract-interaction-details';
import {
  formatTransactionDate,
  formatTransactionDateTime,
  formatTransactionExpirationTimestamp,
} from './utils';

function MetadataItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col py-3 px-6 gap-1">
      <span className="text-xs font-normal text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}

function VerticalDivider() {
  return <div className="h-auto self-stretch w-[1px] bg-border"></div>;
}

interface DetailLabelProps {
  children: React.ReactNode;
  className?: string;
}

function DetailLabel({ children, className }: DetailLabelProps) {
  return (
    <div
      className={`text-sm text-muted-foreground font-normal ${className || ''}`}
    >
      {children}
    </div>
  );
}

interface DetailValueProps {
  children: React.ReactNode;
  className?: string;
}

function DetailValue({ children, className }: DetailValueProps) {
  return (
    <div className={`text-sm font-medium ${className || ''}`}>{children}</div>
  );
}

interface DetailDividerProps {
  className?: string;
}

function DetailDivider({ className }: DetailDividerProps) {
  return (
    <div
      className={`col-span-2 h-[1px] bg-border my-2 ${className || ''}`}
    ></div>
  );
}

/** Single-transaction presentation; the caller owns signing and execution. */
export function SingleTransactionDetailHeader({
  transaction,
  transactionId,
  className,
}: {
  transaction: AccountTransactionChildWithDerivedStatuses;
  transactionId?: string;
  className?: string;
}) {
  const displayStatus =
    transaction.executionStatus ?? transaction.signatureStatus;
  return (
    <SheetHeader
      className={cn(
        'gap-4 border-b sticky -top-6 bg-background left-0 right-0 pb-4 -mx-6 -mt-6 p-6 z-[5]',
        className
      )}
    >
      <SheetTitle className="flex flex-col gap-2">
        <div className="text-xs text-muted-foreground font-normal uppercase tracking-wide pl-4">
          Transaction #{transactionId ?? transaction.id}
        </div>
        <div className="flex items-center gap-6">
          <TransactionDetailTitle transaction={transaction} className="-ml-2" />
        </div>
        <SheetDescription asChild>
          <div className="text-muted-foreground text-base font-normal pl-4">
            {transaction.description}
          </div>
        </SheetDescription>
      </SheetTitle>
      <Card className="w-full flex flex-row items-stretch py-0 gap-0">
        {(() => {
          const approvalProps =
            transaction.policy && displayStatus === 'pendingReviewSignatures'
              ? {
                  currentApprovals: transaction.approvedMembers?.length || 0,
                  requiredApprovals: transaction.policy.requiredApprovals,
                }
              : undefined;

          return (
            <StatusBadge
              status={displayStatus}
              invalidated={transaction.invalidated}
              {...approvalProps}
              className="px-6 rounded-r-none"
            />
          );
        })()}
        <VerticalDivider />
        <MetadataItem
          label="Initiated by"
          value={formatName(transaction.initiator.name)}
        />
        <VerticalDivider />
        <MetadataItem
          label="Initiated on"
          value={formatTransactionDate(transaction.dateProposed)}
        />
        <VerticalDivider />
        <MetadataItem
          label={
            transaction.dateExecuted
              ? 'Executed on'
              : displayStatus === 'expired'
                ? 'Expiry time'
                : 'Expires on'
          }
          value={
            transaction.dateExecuted
              ? formatTransactionDateTime(transaction.dateExecuted)
              : formatTransactionExpirationTimestamp(
                  transaction.expirationTimestamp
                )
          }
        />
      </Card>
    </SheetHeader>
  );
}

/**
 * Render child-level details for one transaction; the caller owns signing and
 * execution. Completed executions hide the current policy-limit usage section.
 */
export function SingleTransactionDetailBody({
  transaction,
}: {
  transaction: AccountTransactionChildWithDerivedStatuses;
}) {
  const isAutoApprovalPolicy = transaction.policy.requiredApprovals === 0;
  const isHistoricalExecutedTransaction =
    transaction.executionStatus === 'completed';
  const policyLimitWindow =
    !isHistoricalExecutedTransaction &&
    transaction.policyLimitWindowStart != null &&
    transaction.policyLimitWindowEnd != null &&
    transaction.policyLimitUsedAmountRaw != null &&
    transaction.policyLimitRemainingAmountRaw != null
      ? {
          start: transaction.policyLimitWindowStart,
          end: transaction.policyLimitWindowEnd,
          usedRaw: transaction.policyLimitUsedAmountRaw,
          remainingRaw: transaction.policyLimitRemainingAmountRaw,
          thresholdRaw: transaction.policyLimitThresholdRaw,
        }
      : null;
  const formatPolicyLimitAmount = (rawAmount: string) =>
    transaction.type === SignableType.TokenTransfer
      ? toHumanReadableTokenAmount(
          rawAmount,
          (transaction as TokenTransferChildWithContext).asset.decimals
        )
      : rawAmount;

  return (
    <>
      <div className="pb-6">
        <SectionHeader title="Description">
          <Card className="border-gray-500 bg-background">
            <CardContent>
              <div className="text-base text-primary font-medium">
                {transaction.description}
              </div>
            </CardContent>
          </Card>
        </SectionHeader>
        <SectionHeader
          title={
            transaction.type === 'CONTRACT_INTERACTION'
              ? 'Contract Interaction'
              : 'Token Transfer'
          }
        >
          {/* Transaction Type Display */}
          <Card className=" bg-background">
            <CardContent>
              <TransactionDetailTypeDisplay
                transaction={transaction}
                variant="detail"
              />

              {(() => {
                if (transaction.type === 'CONTRACT_INTERACTION') {
                  return (
                    <ContractInteractionDetails transaction={transaction} />
                  );
                }
              })()}
            </CardContent>
          </Card>
        </SectionHeader>

        <div className="grid grid-cols-[1fr,auto,1fr] gap-x-4 mt-9">
          {/* Origin Header */}
          <SectionHeaderText title="From" />

          {/* Empty cell for alignment */}
          <div></div>

          {/* Destination Header */}
          <SectionHeaderText title="To" />

          {/* Origin Card */}
          <Card className="h-full">
            <CardContent className="flex h-full flex-1 items-center">
              <OriginDisplay
                account={transaction.account}
                variant="detail"
                centerWhenNoLabel
                showCopy
                showBlockExplorerLink={false}
              />
            </CardContent>
          </Card>

          {/* Arrow in center column, vertically centered */}
          <div className="flex justify-center items-center">
            <ArrowRight className="h-6 w-6 text-muted-foreground" />
          </div>

          {/* Destination Card */}
          <Card className="h-full">
            <CardContent className="flex h-full flex-1 items-center">
              {transaction.type === SignableType.TokenTransfer ? (
                <DestinationDisplay
                  type={SignableType.TokenTransfer}
                  destination={
                    (transaction as TokenTransferChildWithContext).destination
                  }
                  destinationName={
                    (transaction as TokenTransferChildWithContext)
                      .destinationName
                  }
                  networkId={transaction.networkId}
                  variant="detail"
                  centerWhenNoLabel
                  showCopy
                  showBlockExplorerLink={false}
                />
              ) : (
                <DestinationDisplay
                  type={SignableType.ContractInteraction}
                  contractAddress={transaction.toAddress}
                  contractAddressName={transaction.contractAddressName}
                  networkId={transaction.networkId}
                  variant="detail"
                  centerWhenNoLabel
                  showCopy
                  showBlockExplorerLink={false}
                />
              )}
            </CardContent>
          </Card>
        </div>

        {/* Policy Information */}
        <SectionHeader title="Policy">
          <Card>
            <CardContent>
              <div className="grid grid-cols-2 gap-2">
                <DetailLabel>Name</DetailLabel>
                <DetailValue>{transaction.policy.name}</DetailValue>

                {transaction.policy.description && (
                  <>
                    <DetailDivider />

                    <DetailLabel>Description</DetailLabel>
                    <DetailValue className="min-w-0 whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                      {transaction.policy.description}
                    </DetailValue>
                  </>
                )}

                <DetailDivider />

                <DetailLabel>Required Approvals</DetailLabel>
                <DetailValue>
                  {isAutoApprovalPolicy
                    ? 'Only initiator'
                    : `${transaction.policy.requiredApprovals} of ${transaction.policy.totalApprovers}`}
                </DetailValue>
              </div>
            </CardContent>
          </Card>
        </SectionHeader>
        {/* Members */}
        {!isAutoApprovalPolicy && (
          <div className="flex w-full flex-row justify-stretch gap-4">
            {/* Pending Column */}

            <SectionHeader
              title="Pending"
              className="w-full flex flex-1 flex-col items-stretch justify-stretch"
            >
              <Card className="w-full flex-1">
                <CardContent>
                  {transaction.pendingApprovers &&
                  transaction.pendingApprovers.length > 0 ? (
                    transaction.pendingApprovers.map((member, index) => (
                      <div key={member.id}>
                        <MemberDisplay
                          member={member}
                          size="md"
                          variant="simple"
                        />
                        {index <
                          (transaction.pendingApprovers?.length || 0) - 1 && (
                          <div className="h-[1px] bg-border my-2"></div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      No pending members
                    </div>
                  )}
                </CardContent>
              </Card>
            </SectionHeader>

            <SectionHeader
              title="Approved"
              className="w-full flex flex-1 flex-col items-stretch justify-stretch"
            >
              <Card className="w-full flex-1">
                <CardContent>
                  {transaction.approvedMembers &&
                  transaction.approvedMembers.length > 0 ? (
                    transaction.approvedMembers.map((member, index) => (
                      <div key={member.id}>
                        <MemberDisplay
                          member={member}
                          size="md"
                          variant="simple"
                        />
                        {index <
                          (transaction.approvedMembers?.length || 0) - 1 && (
                          <div className="h-[1px] bg-border my-2"></div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      No approvals
                    </div>
                  )}
                </CardContent>
              </Card>
            </SectionHeader>

            <SectionHeader
              title="Rejected"
              className="w-full flex flex-1 flex-col items-stretch justify-stretch"
            >
              <Card className="w-full flex-1">
                <CardContent>
                  {transaction.rejectedMembers &&
                  transaction.rejectedMembers.length > 0 ? (
                    transaction.rejectedMembers.map((member, index) => (
                      <div key={member.id}>
                        <MemberDisplay
                          member={member}
                          size="sm"
                          variant="simple"
                        />
                        {index <
                          (transaction.rejectedMembers?.length || 0) - 1 && (
                          <div className="h-[1px] bg-border my-2"></div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      No rejections
                    </div>
                  )}
                </CardContent>
              </Card>
            </SectionHeader>
          </div>
        )}
      </div>

      {/* Policy limit usage */}
      {policyLimitWindow ? (
        <SectionHeader title="Policy limit usage">
          <Card>
            <CardContent>
              <div className="flex flex-col">
                {(() => {
                  const capRaw =
                    policyLimitWindow.thresholdRaw ??
                    (
                      BigInt(policyLimitWindow.usedRaw) +
                      BigInt(policyLimitWindow.remainingRaw)
                    ).toString();
                  const limitResetAt = new Date(policyLimitWindow.end);
                  return transaction.type === SignableType.TokenTransfer ? (
                    <TokenTransferLimit
                      className="border-0 pt-0 mt-0"
                      policyLimit={formatPolicyLimitAmount(capRaw)}
                      windowUsed={formatPolicyLimitAmount(
                        policyLimitWindow.usedRaw
                      )}
                      currentTransactionAmount={toHumanReadableTokenAmount(
                        transaction.rawAmount,
                        transaction.asset.decimals
                      )}
                      tokenSymbol={transaction.asset.symbol}
                      windowResetAt={limitResetAt}
                    />
                  ) : (
                    <TokenTransferLimit
                      className="border-0 pt-0 mt-0"
                      policyLimit={formatPolicyLimitAmount(capRaw)}
                      windowUsed={formatPolicyLimitAmount(
                        policyLimitWindow.usedRaw
                      )}
                      currentTransactionAmount="0"
                      tokenSymbol=""
                      windowResetAt={limitResetAt}
                    />
                  );
                })()}
              </div>
            </CardContent>
          </Card>
        </SectionHeader>
      ) : null}
    </>
  );
}
